/**
 * 顶部元素工具条：文本 / 图形 / 绘制 / 表格 / 图片等快捷工具；图片为左侧浮层
 */
import {
  IconH1,
  IconH2,
  IconH3,
  IconLetterT,
  IconList,
  IconListNumbers,
  IconSquare,
  IconPhoto,
  IconTable,
  IconWriting,
  IconBrush,
  IconComponents,
} from '@tabler/icons-react';
import type { TextBlockKind } from '@/components/canvas/elements/text/utils/textContent';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Button, Dropdown, Label, Toolbar, Tooltip, cn } from '@heroui/react';
import { getBlockInsertMenuItems, hasBlockDefinition } from '@/components/canvas/blocks/registry/blockRegistry';
import type { BlockType } from '@/components/canvas/blocks/types';
import '@/components/canvas/blocks';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { SHAPE_INSERT_MENU_ITEMS, useShapeDrawMode } from '@/components/canvas/elements/shape/toolbar/ShapeInsertMenu';

const blockInsertMenuItems = getBlockInsertMenuItems();

const TEXT_INSERT_MENU_ITEMS: Array<{
  id: TextBlockKind;
  title: string;
  Icon: React.ComponentType<{ className?: string; stroke?: number }>;
}> = [
  { id: 'h1', title: '一级标题', Icon: IconH1 },
  { id: 'h2', title: '二级标题', Icon: IconH2 },
  { id: 'h3', title: '三级标题', Icon: IconH3 },
  { id: 'paragraph', title: '正文', Icon: IconLetterT },
  { id: 'unordered-list-item', title: '无序列表', Icon: IconList },
  { id: 'ordered-list-item', title: '有序列表', Icon: IconListNumbers },
];

const IMAGE_PANEL_MOTION = {
  initial: { x: '-108%' },
  animate: { x: 0 },
  exit: { x: '-108%' },
  transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const },
};

const IMAGE_PANEL_BACKDROP_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
};

function ImagePanelLayer({
  open,
  container,
  onClose,
  imagePanel,
}: {
  open: boolean;
  container: HTMLElement | null;
  onClose: () => void;
  imagePanel?: React.ReactNode;
}) {
  if (!container) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.button
            key="image-panel-backdrop"
            type="button"
            className="absolute inset-0 z-[54] cursor-default border-0 bg-transparent p-0"
            aria-label="关闭图片面板"
            onClick={onClose}
            {...IMAGE_PANEL_BACKDROP_MOTION}
          />
            <motion.aside
            key="image-panel-aside"
            className="absolute bottom-2 left-2 top-14 z-[55] flex w-[min(92vw,280px)] max-w-[90vw] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-lg"
            role="dialog"
            aria-label="图片"
            {...IMAGE_PANEL_MOTION}
          >
            <header className="shrink-0 border-b border-border px-4 py-3">
              <h2 className="text-base font-medium text-default-foreground">图片</h2>
            </header>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {imagePanel ?? (
                <div className="flex flex-1 items-center justify-center p-4 text-sm text-muted">
                  未配置图片面板
                </div>
              )}
            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    container,
  );
}

export interface ElementPanelProps {
  /** 画布设计器根节点：图片浮层挂载于此，与 DragOverlay 同层叠上下文 */
  overlayContainerRef?: React.RefObject<HTMLElement | null>;
  /** 图片侧栏内容，由业务容器注入 */
  imagePanel?: React.ReactNode;
  /** 关闭右侧属性 Popover（插入工具激活时） */
  onDismissPropertyPanel?: () => void;
  /** 注册关闭图片侧栏，供属性 Popover 打开时调用 */
  onRegisterCloseImageSheet?: (close: () => void) => void;
  className?: string;
  style?: React.CSSProperties;
}

export function ElementPanel({
  overlayContainerRef,
  imagePanel,
  onDismissPropertyPanel,
  onRegisterCloseImageSheet,
  className,
  style,
}: ElementPanelProps) {
  const {
    textDrawMode,
    setTextDrawMode,
    shapeDrawKind,
    setShapeDrawKind,
    penToolActive,
    setPenToolActive,
    freehandToolActive,
    setFreehandToolActive,
    tableDrawMode,
    setTableDrawMode,
    addBlock,
  } = useCanvas();
  const enterShapeDrawMode = useShapeDrawMode();

  const [imageSheetOpen, setImageSheetOpen] = useState(false);

  useEffect(() => {
    onRegisterCloseImageSheet?.(() => setImageSheetOpen(false));
  }, [onRegisterCloseImageSheet]);

  const dismissPanelOverlays = () => {
    onDismissPropertyPanel?.();
    setImageSheetOpen(false);
  };

  const deactivateDrawingTools = () => {
    setTextDrawMode(null);
    setTableDrawMode(false);
    setShapeDrawKind(null);
    if (penToolActive) setPenToolActive(false);
    if (freehandToolActive) setFreehandToolActive(false);
  };

  const handlePenToolPress = () => {
    if (penToolActive) {
      setPenToolActive(false);
      return;
    }
    dismissPanelOverlays();
    setTextDrawMode(null);
    setShapeDrawKind(null);
    setPenToolActive(true);
  };

  const handleFreehandToolPress = () => {
    if (freehandToolActive) {
      setFreehandToolActive(false);
      return;
    }
    dismissPanelOverlays();
    setTextDrawMode(null);
    setShapeDrawKind(null);
    setFreehandToolActive(true);
  };

  const handleTableToolPress = () => {
    if (tableDrawMode) {
      setTableDrawMode(false);
      return;
    }
    dismissPanelOverlays();
    setTextDrawMode(null);
    setShapeDrawKind(null);
    if (penToolActive) setPenToolActive(false);
    if (freehandToolActive) setFreehandToolActive(false);
    setTableDrawMode(true);
  };

  const handleOpenImageSheet = () => {
    onDismissPropertyPanel?.();
    deactivateDrawingTools();
    setImageSheetOpen(true);
  };

  return (
    <div
      className={cn(
        'element-panel absolute left-1/2 top-2 z-[50] flex h-12 min-h-0 min-w-0 w-max max-w-[calc(100vw-1rem)] shrink-0 -translate-x-1/2 justify-center',
        className,
      )}
      style={style}
    >
      <Toolbar aria-label="插入元素" className="px-2 border border-border shadow-none rounded-4xl bg-surface">
        <div className="flex w-full min-w-0 items-center gap-1 overflow-x-auto">
          <Dropdown>
            <Button
              variant={!!textDrawMode ? 'primary' : 'ghost'}
              size="sm"
              isIconOnly
              aria-label="文本元素"
              aria-pressed={!!textDrawMode}
            >
              <IconLetterT stroke={1.5} />
            </Button>
            <Dropdown.Popover className="min-w-44" placement="bottom start" offset={6}>
              <Dropdown.Menu
                aria-label="文本元素"
                onAction={(key) => {
                  dismissPanelOverlays();
                  if (penToolActive) setPenToolActive(false);
                  if (freehandToolActive) setFreehandToolActive(false);
                  setShapeDrawKind(null);
                  setTableDrawMode(false);
                  setTextDrawMode(key as TextBlockKind);
                }}
              >
                {TEXT_INSERT_MENU_ITEMS.map(({ id, title, Icon }) => (
                  <Dropdown.Item key={id} id={id} textValue={title}>
                    <Label className="flex w-full items-center gap-2 font-normal">
                      <Icon className="size-4 shrink-0" stroke={1.5} />
                      {title}
                    </Label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>

          <Dropdown>
            <Button
              variant={shapeDrawKind ? 'primary' : 'ghost'}
              size="sm"
              isIconOnly
              aria-label="图形元素"
              aria-pressed={!!shapeDrawKind}
            >
              <IconSquare stroke={1.5} />
            </Button>
            <Dropdown.Popover className="min-w-44" placement="bottom start" offset={6}>
              <Dropdown.Menu
                aria-label="图形元素"
                onAction={(key) => {
                  dismissPanelOverlays();
                  if (penToolActive) setPenToolActive(false);
                  if (freehandToolActive) setFreehandToolActive(false);
                  setTextDrawMode(null);
                  setTableDrawMode(false);
                  enterShapeDrawMode(String(key));
                }}
              >
                {SHAPE_INSERT_MENU_ITEMS.map(({ id, title, Icon }) => (
                  <Dropdown.Item key={id} id={id} textValue={title}>
                    <Label className="flex w-full items-center gap-2 font-normal">
                      <Icon className="size-4 shrink-0" stroke={1.5} />
                      {title}
                    </Label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>

          <Button
            type="button"
            variant={penToolActive ? 'primary' : 'ghost'}
            size="sm"
            isIconOnly={!penToolActive}
            className={cn('shrink-0', penToolActive && 'gap-1.5 px-2.5')}
            aria-label={penToolActive ? '结束绘制' : '钢笔路径'}
            aria-pressed={penToolActive}
            onPress={handlePenToolPress}
          >
            <IconWriting className="size-4 shrink-0" stroke={1.25} />
            {penToolActive ? (
              <span className="whitespace-nowrap text-sm">结束绘制</span>
            ) : null}
          </Button>

          <Button
            type="button"
            variant={freehandToolActive ? 'primary' : 'ghost'}
            size="sm"
            isIconOnly={!freehandToolActive}
            className={cn('shrink-0', freehandToolActive && 'gap-1.5 px-2.5')}
            aria-label={freehandToolActive ? '结束涂抹' : '涂抹笔'}
            aria-pressed={freehandToolActive}
            onPress={handleFreehandToolPress}
          >
            <IconBrush className="size-4 shrink-0" stroke={1.25} />
            {freehandToolActive ? (
              <span className="whitespace-nowrap text-sm">结束涂抹</span>
            ) : null}
          </Button>

          <Tooltip>
            <Tooltip.Trigger>
              <Button
                type="button"
                variant={tableDrawMode ? 'primary' : 'ghost'}
                size="sm"
                isIconOnly
                className="size-9 shrink-0"
                aria-label={tableDrawMode ? '结束插入表格' : '拖拽插入表格'}
                aria-pressed={tableDrawMode}
                onPress={handleTableToolPress}
              >
                <IconTable className="size-4 shrink-0" stroke={1.25} />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content placement="bottom" offset={6}>
              {tableDrawMode ? '在画布拖拽框选表格区域' : '拖拽插入表格'}
            </Tooltip.Content>
          </Tooltip>

          <Tooltip>
            <Tooltip.Trigger>
              <Button
                type="button"
                variant={imageSheetOpen ? 'primary' : 'ghost'}
                size="sm"
                isIconOnly
                className="size-9 shrink-0"
                aria-label="图片"
                aria-pressed={imageSheetOpen}
                onPress={() => (imageSheetOpen ? setImageSheetOpen(false) : handleOpenImageSheet())}
              >
                <IconPhoto className="size-4 shrink-0" stroke={1.25} />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content placement="bottom" offset={6}>
              插入图片
            </Tooltip.Content>
          </Tooltip>

          <Dropdown>
            <Button
              variant="ghost"
              size="sm"
              isIconOnly
              aria-label="功能块"
            >
              <IconComponents stroke={1.5} />
            </Button>
            <Dropdown.Popover className="min-w-44" placement="bottom start" offset={6}>
              <Dropdown.Menu
                aria-label="功能块"
                onAction={(key) => {
                  const type = String(key) as BlockType;
                  if (!hasBlockDefinition(type)) return;
                  dismissPanelOverlays();
                  deactivateDrawingTools();
                  addBlock(type);
                }}
              >
                {blockInsertMenuItems.map((item) => (
                  <Dropdown.Item key={item.type} id={item.type} textValue={item.label}>
                    <Label className="flex w-full items-center gap-2 font-normal">
                      <item.Icon className="size-4 shrink-0" stroke={1.5} />
                      {item.label}
                    </Label>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>
      </Toolbar>

      <ImagePanelLayer
        open={imageSheetOpen}
        container={overlayContainerRef?.current ?? null}
        onClose={() => setImageSheetOpen(false)}
        imagePanel={imagePanel}
      />
    </div>
  );
}
